/**
 * Admin v2 · shared UI helpers.
 *
 * Small, framework-free primitives that every screen reuses: money / bps
 * formatting, KPI cards (windowed + snapshot variants), skeleton cards for
 * the loading state, a dense empty state, tiny inline SVG chart helpers,
 * date-range controls, CSV export button, and an asyncPanel wrapper.
 *
 * Every helper returns an HTML string so screens can compose freely and
 * only mount the whole payload once — no fine-grained render bookkeeping.
 */

import { api, ApiError } from '../../api.js';
import { auth } from '../../auth.js';
import { escape } from '../../components/form.js';
import { toast } from '../../components/toast.js';
import { navigate } from '../../router.js';

const API_BASE = import.meta.env.VITE_API_BASE_URL || '';

// ---------------------------------------------------------------------------
// Formatters
// ---------------------------------------------------------------------------

const _naira = new Intl.NumberFormat('en-NG', {
  style: 'currency',
  currency: 'NGN',
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});
const _int = new Intl.NumberFormat('en-NG');

/** kobo → "₦12,345". Zero-fraction — the finance team scans big numbers,
 *  not banks. Nullish falls back to the em-dash placeholder. */
export function fmtKobo(kobo) {
  if (kobo == null) return '—';
  const n = Math.trunc(Number(kobo));
  if (!Number.isFinite(n)) return '—';
  return _naira.format(n / 100);
}

/** Basis points → "12.4%". Rounds to one decimal. */
export function fmtBps(bps) {
  if (bps == null) return '—';
  const n = Number(bps);
  if (!Number.isFinite(n)) return '—';
  return `${(n / 100).toFixed(1)}%`;
}

/** Plain int with thousands separators. */
export function fmtInt(n) {
  if (n == null) return '—';
  const v = Number(n);
  if (!Number.isFinite(v)) return '—';
  return _int.format(v);
}

/** ISO → "2 hrs ago" / absolute for older values. */
export function fmtRelTime(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const diffSec = Math.max(0, Math.round((Date.now() - d.getTime()) / 1000));
  if (diffSec < 60) return 'just now';
  if (diffSec < 3600) return `${Math.floor(diffSec / 60)}m ago`;
  if (diffSec < 86400) return `${Math.floor(diffSec / 3600)}h ago`;
  if (diffSec < 30 * 86400) return `${Math.floor(diffSec / 86400)}d ago`;
  return d.toLocaleDateString('en-NG', { year: 'numeric', month: 'short', day: 'numeric' });
}

/** ISO → "Jun 2026". Used on player-detail "since" badge. */
export function fmtMonthYear(iso) {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-NG', { month: 'short', year: 'numeric' });
}

// ---------------------------------------------------------------------------
// KPI cards (windowed + snapshot)
// ---------------------------------------------------------------------------

/**
 * Standard windowed KPI card.
 * opts: { label, value, hint?, spark? (path d string), hero? (bool),
 *         delta?: { arrow: '▲'|'▼', text: '12.4%', tone: 'g'|'r'|'n' } }
 */
export function kpi({ label, value, hint = '', spark = '', hero = false, delta = null } = {}) {
  const cls = hero ? 'adm2-kpi hero' : 'adm2-kpi';
  const chip = delta
    ? `<span class="adm2-chip ${delta.tone === 'g' ? 'g' : delta.tone === 'r' ? 'r' : ''}">${escape(delta.arrow || '')} ${escape(delta.text || '')}</span>`
    : '<span class="adm2-chip">·</span>';
  const sparkSvg = spark
    ? `<svg width="78" height="26" viewBox="0 0 100 32" fill="none" preserveAspectRatio="none">
         <path d="${escape(spark)}" style="stroke:var(--a-brand)" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"></path>
       </svg>`
    : '';
  return `
    <div class="${cls}">
      <div class="adm2-kpi-label">${escape(label)}</div>
      <div class="adm2-kpi-value">${escape(value)}</div>
      ${hint ? `<div class="adm2-kpi-hint">${escape(hint)}</div>` : ''}
      <div class="adm2-kpi-foot">${chip}${sparkSvg}</div>
    </div>
  `;
}

/**
 * Snapshot KPI — warm background + "SNAPSHOT" pill. Used for balances that
 * ignore the date range (outstanding liability, wallet balance, etc.).
 */
export function snapshotKpi({ label, value, hint = '' } = {}) {
  return `
    <div class="adm2-kpi snapshot">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:8px;">
        <div class="adm2-kpi-label">${escape(label)}</div>
        <span class="adm2-kpi-badge">SNAPSHOT</span>
      </div>
      <div class="adm2-kpi-value">${escape(value)}</div>
      ${hint ? `<div class="adm2-kpi-hint">${escape(hint)}</div>` : ''}
    </div>
  `;
}

// ---------------------------------------------------------------------------
// Charts (all inline SVG — no libraries, keeps bundle flat)
// ---------------------------------------------------------------------------

/**
 * points: array of numbers. Returns an SVG `d` string (rescaled to a
 * 100x32 box) for a smooth polyline sparkline — used inside kpi cards.
 */
export function sparklinePath(points) {
  if (!points || points.length < 2) return 'M0 26 L100 26';
  const min = Math.min(...points);
  const max = Math.max(...points);
  const span = Math.max(1, max - min);
  const step = 100 / (points.length - 1);
  return points
    .map((v, i) => {
      const x = i * step;
      const y = 32 - ((v - min) / span) * 26 - 3;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(1)} ${y.toFixed(1)}`;
    })
    .join(' ');
}

/**
 * Full-width line chart. `series` is [{name, color, values: [num,...]}].
 * All series must have the same number of points (or we treat missing as 0).
 * `xLabels` is a list of tick labels (7 evenly spaced typically).
 */
export function lineChart({ series, xLabels = [], height = 240 } = {}) {
  const w = 760;
  const h = height;
  const pad = { l: 0, r: 0, t: 20, b: 20 };
  if (!series || !series.length) return emptyState('No chart data');

  const cols = Math.max(...series.map((s) => s.values.length));
  const flat = series.flatMap((s) => s.values);
  const min = Math.min(0, ...flat);
  const max = Math.max(1, ...flat);
  const span = Math.max(1, max - min);
  const xStep = (w - pad.l - pad.r) / Math.max(1, cols - 1);
  const y = (v) => pad.t + (h - pad.t - pad.b) - ((v - min) / span) * (h - pad.t - pad.b);

  const gridY = [0.25, 0.5, 0.75, 1].map((f) => Math.round(pad.t + (h - pad.t - pad.b) * f));
  const paths = series
    .map((s) => {
      const d = s.values
        .map((v, i) => `${i === 0 ? 'M' : 'L'}${(pad.l + i * xStep).toFixed(1)} ${y(v).toFixed(1)}`)
        .join(' ');
      const dash = s.dashed ? 'stroke-dasharray="5 5"' : '';
      return `<path d="${d}" fill="none" stroke="${escape(s.color)}" stroke-width="2.4" stroke-linecap="round" vector-effect="non-scaling-stroke" ${dash}></path>`;
    })
    .join('');
  const legend = series
    .map(
      (s) =>
        `<span><i style="background:${escape(s.color)};${s.dashed ? 'border-top:2px dashed ' + escape(s.color) + ';background:transparent;height:0;' : ''}"></i>${escape(s.name)}</span>`,
    )
    .join('');
  const ticks = xLabels.map((t) => `<span>${escape(t)}</span>`).join('');
  return `
    <div>
      <div class="adm2-legend" style="margin-bottom:12px;">${legend}</div>
      <svg viewBox="0 0 ${w} ${h}" width="100%" height="${h}" preserveAspectRatio="none" style="display:block">
        ${gridY.map((yy) => `<line x1="0" y1="${yy}" x2="${w}" y2="${yy}" stroke="rgba(127,143,170,.16)" stroke-width="1"></line>`).join('')}
        ${paths}
      </svg>
      ${ticks ? `<div class="adm2-xticks">${ticks}</div>` : ''}
    </div>
  `;
}

/**
 * Interactive single-series bar chart. Mounts directly into `host` because
 * we need pointer handlers for the hover tooltip.
 *
 * opts: {
 *   data: [{ x: string|Date, y: number, meta?: object }, ...],
 *   color?: string,          // bar fill (default var(--a-gold))
 *   height?: number,         // svg height in px (default 200)
 *   yLabel?: string,         // shown on the tooltip line
 *   valueFmt?: (v)=>string,  // tooltip value formatter (default fmtInt)
 *   xFmt?: (x)=>string,      // tooltip x-axis formatter (default identity)
 *   tooltipExtra?: (row)=>string,   // extra lines in the tooltip (html)
 *   emptyMessage?: string,
 * }
 */
export function mountInteractiveBarChart(
  host,
  {
    data = [],
    color = 'var(--a-warm-ink)',
    height = 200,
    yLabel = '',
    valueFmt = fmtInt,
    xFmt = (x) => String(x ?? ''),
    tooltipExtra,
    emptyMessage = 'No data for the selected range.',
  } = {},
) {
  if (!host) return;
  if (!data.length) {
    host.innerHTML = emptyState(emptyMessage);
    return;
  }
  const w = 760;
  const h = height;
  const pad = { t: 12, r: 6, b: 26, l: 6 };
  const innerW = w - pad.l - pad.r;
  const innerH = h - pad.t - pad.b;
  const max = Math.max(1, ...data.map((d) => Number(d.y || 0)));
  const step = innerW / data.length;
  const barW = Math.max(2, Math.min(20, step * 0.6));
  const bars = data
    .map((d, i) => {
      const val = Number(d.y || 0);
      const bh = Math.max(val > 0 ? 2 : 0, (val / max) * innerH);
      const x = pad.l + i * step + (step - barW) / 2;
      const y = pad.t + innerH - bh;
      return `<rect data-bar-i="${i}" x="${x.toFixed(1)}" y="${y.toFixed(1)}" width="${barW.toFixed(1)}" height="${bh.toFixed(1)}" rx="2" ry="2" fill="${escape(color)}" style="cursor:pointer;transition:opacity .12s;"></rect>`;
    })
    .join('');
  const baseY = pad.t + innerH + 0.5;
  const firstLabel = xFmt(data[0].x);
  const lastLabel = xFmt(data[data.length - 1].x);
  host.innerHTML = `
    <div class="adm2-bar-wrap">
      <svg viewBox="0 0 ${w} ${h}" width="100%" height="${h}" preserveAspectRatio="none" style="display:block;overflow:visible;">
        <line x1="${pad.l}" y1="${baseY}" x2="${w - pad.r}" y2="${baseY}" stroke="rgba(127,143,170,.35)" stroke-width="1" stroke-dasharray="4 6"></line>
        ${bars}
      </svg>
      <div class="adm2-bar-axis">
        <span>${escape(firstLabel)}</span>
        <span>${escape(lastLabel)}</span>
      </div>
      <div class="adm2-bar-tooltip" aria-hidden="true"></div>
    </div>
  `;
  const wrap = host.querySelector('.adm2-bar-wrap');
  const tip = host.querySelector('.adm2-bar-tooltip');
  const rects = host.querySelectorAll('rect[data-bar-i]');
  const clear = () => {
    tip.style.opacity = '0';
    rects.forEach((r) => (r.style.opacity = '1'));
  };
  rects.forEach((r) => {
    r.addEventListener('mouseenter', (ev) => {
      const i = Number(r.dataset.barI);
      const row = data[i];
      if (!row) return;
      rects.forEach((rr) => (rr.style.opacity = rr === r ? '1' : '0.35'));
      const extra = typeof tooltipExtra === 'function' ? tooltipExtra(row) : '';
      tip.innerHTML = `
        <div class="adm2-bar-tt-x">${escape(xFmt(row.x))}</div>
        <div class="adm2-bar-tt-y">${escape(valueFmt(row.y))}${yLabel ? ` <span class="adm2-bar-tt-l">${escape(yLabel)}</span>` : ''}</div>
        ${extra ? `<div class="adm2-bar-tt-extra">${extra}</div>` : ''}
      `;
      tip.style.opacity = '1';
      const wrapRect = wrap.getBoundingClientRect();
      const barRect = r.getBoundingClientRect();
      // Position tooltip above the bar; clamp inside wrapRect.
      const tipRect = tip.getBoundingClientRect();
      let left = barRect.left - wrapRect.left + barRect.width / 2 - tipRect.width / 2;
      left = Math.max(4, Math.min(left, wrapRect.width - tipRect.width - 4));
      const top = barRect.top - wrapRect.top - tipRect.height - 8;
      tip.style.left = `${left}px`;
      tip.style.top = `${top < 0 ? barRect.bottom - wrapRect.top + 6 : top}px`;
    });
    r.addEventListener('mouseleave', clear);
  });
  wrap.addEventListener('mouseleave', clear);
}

/**
 * Interactive multi-series line chart. Mount variant of lineChart —
 * renders the same SVG plus a hover crosshair + tooltip listing each
 * series' value at the pointed x-position.
 *
 * opts (matches lineChart + interactivity extras):
 *   series: [{ name, color, values: [num,...], dashed?, valueFmt?(v)=>string }]
 *   xLabels: string[]  (used both for the axis and the tooltip title)
 *   height: number (default 240)
 *   emptyMessage: string
 */
export function mountLineChart(
  host,
  { series = [], xLabels = [], height = 240, emptyMessage = 'No chart data' } = {},
) {
  if (!host) return;
  if (!series.length || !series[0].values || !series[0].values.length) {
    host.innerHTML = emptyState(emptyMessage);
    return;
  }
  const w = 760;
  const h = height;
  const pad = { l: 0, r: 0, t: 20, b: 20 };
  const cols = Math.max(...series.map((s) => s.values.length));
  const flat = series.flatMap((s) => s.values);
  const min = Math.min(0, ...flat);
  const max = Math.max(1, ...flat);
  const span = Math.max(1, max - min);
  const xStep = (w - pad.l - pad.r) / Math.max(1, cols - 1);
  const y = (v) => pad.t + (h - pad.t - pad.b) - ((v - min) / span) * (h - pad.t - pad.b);

  const gridY = [0.25, 0.5, 0.75, 1].map((f) => Math.round(pad.t + (h - pad.t - pad.b) * f));
  const paths = series
    .map((s) => {
      const d = s.values
        .map((v, i) => `${i === 0 ? 'M' : 'L'}${(pad.l + i * xStep).toFixed(1)} ${y(v).toFixed(1)}`)
        .join(' ');
      const dash = s.dashed ? 'stroke-dasharray="5 5"' : '';
      return `<path d="${d}" fill="none" stroke="${escape(s.color)}" stroke-width="2.4" stroke-linecap="round" vector-effect="non-scaling-stroke" ${dash}></path>`;
    })
    .join('');
  const legend = series
    .map(
      (s) =>
        `<span><i style="background:${escape(s.color)};${s.dashed ? 'border-top:2px dashed ' + escape(s.color) + ';background:transparent;height:0;' : ''}"></i>${escape(s.name)}</span>`,
    )
    .join('');
  // Sparse axis ticks — sample ~7 evenly-spaced labels so a 90-day range
  // doesn't render 90 ticks. Full xLabels still drives the tooltip.
  const _sampleTicks = (arr) => {
    if (!arr || !arr.length) return [];
    if (arr.length <= 7) return arr.slice();
    const stepT = (arr.length - 1) / 6;
    const out = [];
    for (let k = 0; k < 7; k += 1) out.push(arr[Math.round(k * stepT)]);
    return out;
  };
  const ticks = _sampleTicks(xLabels).map((t) => `<span>${escape(t)}</span>`).join('');

  // Interaction: an invisible <rect> hit-target under each column; on
  // pointerenter we place the crosshair line + dots and populate the
  // tooltip. Using a full-width column keeps hover forgiving on touch
  // and dense series.
  const hitAreaW = xStep;
  const hitRects = series[0].values
    .map((_, i) => {
      const cx = pad.l + i * xStep;
      const rx = Math.max(0, cx - hitAreaW / 2);
      return `<rect data-lc-i="${i}" x="${rx.toFixed(1)}" y="0" width="${hitAreaW.toFixed(1)}" height="${h}" fill="transparent" style="cursor:crosshair;"></rect>`;
    })
    .join('');
  const dots = series
    .map(
      (s) =>
        `<circle data-lc-dot="${escape(s.name)}" r="4.5" cx="-10" cy="-10" fill="${escape(s.color)}" stroke="var(--a-panel)" stroke-width="2" style="pointer-events:none;opacity:0;"></circle>`,
    )
    .join('');

  host.innerHTML = `
    <div class="adm2-lc-wrap">
      <div class="adm2-legend" style="margin-bottom:12px;">${legend}</div>
      <div style="position:relative;">
        <svg class="adm2-lc-svg" viewBox="0 0 ${w} ${h}" width="100%" height="${h}" preserveAspectRatio="none" style="display:block;overflow:visible;">
          ${gridY.map((yy) => `<line x1="0" y1="${yy}" x2="${w}" y2="${yy}" stroke="rgba(127,143,170,.16)" stroke-width="1"></line>`).join('')}
          ${paths}
          <line class="adm2-lc-crosshair" x1="-10" y1="${pad.t}" x2="-10" y2="${h - pad.b}" stroke="rgba(127,143,170,.55)" stroke-width="1" stroke-dasharray="4 4" style="opacity:0;pointer-events:none;"></line>
          ${dots}
          ${hitRects}
        </svg>
        <div class="adm2-lc-tooltip" aria-hidden="true"></div>
      </div>
      ${ticks ? `<div class="adm2-xticks">${ticks}</div>` : ''}
    </div>
  `;

  const svg = host.querySelector('.adm2-lc-svg');
  const wrap = host.querySelector('.adm2-lc-wrap');
  const tip = host.querySelector('.adm2-lc-tooltip');
  const crosshair = host.querySelector('.adm2-lc-crosshair');
  const dotEls = Array.from(host.querySelectorAll('circle[data-lc-dot]'));
  const hits = host.querySelectorAll('rect[data-lc-i]');

  const clear = () => {
    tip.style.opacity = '0';
    crosshair.style.opacity = '0';
    dotEls.forEach((d) => (d.style.opacity = '0'));
  };
  hits.forEach((r) => {
    r.addEventListener('mouseenter', () => {
      const i = Number(r.dataset.lcI);
      const cx = pad.l + i * xStep;
      crosshair.setAttribute('x1', String(cx));
      crosshair.setAttribute('x2', String(cx));
      crosshair.style.opacity = '1';
      series.forEach((s, idx) => {
        const dot = dotEls[idx];
        if (!dot) return;
        const v = s.values[i];
        if (v == null || !Number.isFinite(Number(v))) {
          dot.style.opacity = '0';
          return;
        }
        dot.setAttribute('cx', String(cx));
        dot.setAttribute('cy', String(y(v)));
        dot.style.opacity = '1';
      });
      const rows = series
        .map((s) => {
          const v = s.values[i];
          const disp = typeof s.valueFmt === 'function' ? s.valueFmt(v) : fmtInt(v);
          return `<div class="adm2-lc-row"><span class="adm2-lc-dot" style="background:${escape(s.color)};"></span><span class="adm2-lc-name">${escape(s.name)}</span><span class="adm2-lc-val">${escape(disp)}</span></div>`;
        })
        .join('');
      const title = xLabels[i] != null ? xLabels[i] : `#${i + 1}`;
      tip.innerHTML = `<div class="adm2-lc-title">${escape(title)}</div>${rows}`;
      tip.style.opacity = '1';
      // Position — put the tip above the crosshair, clamped to the wrap.
      const wrapRect = wrap.getBoundingClientRect();
      const svgRect = svg.getBoundingClientRect();
      // Map viewbox x to pixels within svgRect.
      const px = svgRect.left + (cx / w) * svgRect.width - wrapRect.left;
      const tipRect = tip.getBoundingClientRect();
      let left = px - tipRect.width / 2;
      left = Math.max(4, Math.min(left, wrapRect.width - tipRect.width - 4));
      tip.style.left = `${left}px`;
      tip.style.top = `4px`;
    });
    r.addEventListener('mouseleave', clear);
  });
  wrap.addEventListener('mouseleave', clear);
}

/**
 * Donut / pie chart — one slice per input slice, sized by value.
 *
 * opts:
 *   slices: [{ label: string, value: number, color: string }]
 *   size: number  (svg width + height, default 220)
 *   inner: number  (0=filled pie, 0.6=donut hole ratio; default 0.55)
 *   valueFmt(v)=>string  (formats each slice's value in the legend)
 *   emptyMessage: string
 *
 * Zero-value slices are dropped silently so a source that hasn't
 * earned anything yet doesn't render a phantom label. All-zero renders
 * the empty state.
 */
export function mountPieChart(
  host,
  {
    slices = [],
    size = 220,
    inner = 0.55,
    valueFmt = (v) => String(v),
    emptyMessage = 'No data',
  } = {},
) {
  if (!host) return;
  const shown = slices.filter((s) => Number(s.value) > 0);
  const total = shown.reduce((n, s) => n + Number(s.value), 0);
  if (!shown.length || total <= 0) {
    host.innerHTML = emptyState(emptyMessage);
    return;
  }
  const cx = size / 2;
  const cy = size / 2;
  const r = size / 2 - 2;
  const rInner = r * inner;
  // For a single 100%-slice, SVG arc-path with startAngle == endAngle
  // degenerates. Draw a full ring in that case.
  let paths;
  if (shown.length === 1) {
    const s = shown[0];
    paths = `
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="${s.color}"/>
      ${inner > 0 ? `<circle cx="${cx}" cy="${cy}" r="${rInner}" fill="var(--a-bg)"/>` : ''}
    `;
  } else {
    let angle = -Math.PI / 2; // 12 o'clock
    paths = shown
      .map((s) => {
        const frac = Number(s.value) / total;
        const next = angle + frac * 2 * Math.PI;
        const large = frac > 0.5 ? 1 : 0;
        const x1 = cx + r * Math.cos(angle);
        const y1 = cy + r * Math.sin(angle);
        const x2 = cx + r * Math.cos(next);
        const y2 = cy + r * Math.sin(next);
        const xi1 = cx + rInner * Math.cos(next);
        const yi1 = cy + rInner * Math.sin(next);
        const xi2 = cx + rInner * Math.cos(angle);
        const yi2 = cy + rInner * Math.sin(angle);
        const d = inner > 0
          ? `M ${x1.toFixed(2)} ${y1.toFixed(2)}
             A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}
             L ${xi1.toFixed(2)} ${yi1.toFixed(2)}
             A ${rInner} ${rInner} 0 ${large} 0 ${xi2.toFixed(2)} ${yi2.toFixed(2)}
             Z`
          : `M ${cx} ${cy}
             L ${x1.toFixed(2)} ${y1.toFixed(2)}
             A ${r} ${r} 0 ${large} 1 ${x2.toFixed(2)} ${y2.toFixed(2)}
             Z`;
        angle = next;
        const title = `${s.label}: ${valueFmt(s.value)} (${((frac * 100).toFixed(1))}%)`;
        return `<path d="${d}" fill="${s.color}"><title>${escape(title)}</title></path>`;
      })
      .join('');
  }
  const legend = shown
    .map((s) => {
      const pct = ((Number(s.value) / total) * 100).toFixed(1);
      return `
        <div style="display:flex;align-items:center;gap:8px;font:500 12px 'IBM Plex Sans',sans-serif;">
          <span style="display:inline-block;width:11px;height:11px;border-radius:3px;background:${s.color};flex-shrink:0;"></span>
          <span style="flex:1;min-width:0;color:var(--a-ink-2);">${escape(s.label)}</span>
          <span style="font:600 12px 'IBM Plex Mono',monospace;color:var(--a-ink);white-space:nowrap;">${escape(valueFmt(s.value))}</span>
          <span style="font:500 11px 'IBM Plex Mono',monospace;color:var(--a-faint);width:44px;text-align:right;">${pct}%</span>
        </div>
      `;
    })
    .join('');
  host.innerHTML = `
    <div style="display:flex;align-items:center;gap:24px;flex-wrap:wrap;">
      <svg viewBox="0 0 ${size} ${size}" width="${size}" height="${size}"
           style="flex-shrink:0;display:block;">
        ${paths}
      </svg>
      <div style="flex:1;min-width:220px;display:flex;flex-direction:column;gap:8px;">
        ${legend}
      </div>
    </div>
  `;
}


/**
 * Interactive diverging bars — mount variant. Each bar shows a small
 * tooltip with the up + down values on hover.
 *
 * opts:
 *   bars: [{ up, down, label?: string }]
 *   upColor, downColor: string
 *   height: number
 *   valueFmt(v)=>string  (default fmtInt — pass fmtKobo for money)
 *   emptyMessage: string
 */
export function mountDivergingBars(
  host,
  {
    bars = [],
    upColor = 'var(--a-ok)',
    downColor = '#E08177',
    height = 184,
    valueFmt = fmtInt,
    upLabel = 'In',
    downLabel = 'Out',
    xLabels = [],
    emptyMessage = 'No flow data',
  } = {},
) {
  if (!host) return;
  if (!bars.length) {
    host.innerHTML = emptyState(emptyMessage);
    return;
  }
  const maxAbs = Math.max(
    1,
    ...bars.map((b) => Math.max(Math.abs(b.up || 0), Math.abs(b.down || 0))),
  );
  const half = height / 2;
  const cols = bars
    .map((b, i) => {
      const up = Math.round(((b.up || 0) / maxAbs) * (half - 4));
      const down = Math.round(((b.down || 0) / maxAbs) * (half - 4));
      return `
        <div class="adm2-db-col" data-db-i="${i}" style="flex:1;display:flex;flex-direction:column;height:${height}px;cursor:pointer;">
          <div style="flex:1;display:flex;align-items:flex-end;justify-content:center;">
            <div class="adm2-db-up" style="width:62%;height:${up}px;background:${upColor};border-radius:3px 3px 0 0;transition:opacity .12s;"></div>
          </div>
          <div style="height:1px;background:var(--a-border);"></div>
          <div style="flex:1;display:flex;align-items:flex-start;justify-content:center;">
            <div class="adm2-db-down" style="width:62%;height:${down}px;background:${downColor};border-radius:0 0 3px 3px;transition:opacity .12s;"></div>
          </div>
        </div>
      `;
    })
    .join('');
  host.innerHTML = `
    <div class="adm2-db-wrap">
      <div style="display:flex;align-items:stretch;gap:7px;">${cols}</div>
      <div class="adm2-db-tooltip" aria-hidden="true"></div>
    </div>
  `;
  const wrap = host.querySelector('.adm2-db-wrap');
  const tip = host.querySelector('.adm2-db-tooltip');
  const colEls = Array.from(host.querySelectorAll('.adm2-db-col'));
  const allBars = Array.from(host.querySelectorAll('.adm2-db-up, .adm2-db-down'));
  const clear = () => {
    tip.style.opacity = '0';
    allBars.forEach((b) => (b.style.opacity = '1'));
  };
  colEls.forEach((col) => {
    col.addEventListener('mouseenter', () => {
      const i = Number(col.dataset.dbI);
      const bar = bars[i];
      if (!bar) return;
      allBars.forEach((b) => (b.style.opacity = col.contains(b) ? '1' : '0.35'));
      const net = (bar.up || 0) - (bar.down || 0);
      const netTone = net >= 0 ? 'var(--a-ok)' : 'var(--a-bad)';
      const title = bar.label || xLabels[i] || `#${i + 1}`;
      tip.innerHTML = `
        <div class="adm2-lc-title">${escape(title)}</div>
        <div class="adm2-lc-row"><span class="adm2-lc-dot" style="background:${escape(upColor)};"></span><span class="adm2-lc-name">${escape(upLabel)}</span><span class="adm2-lc-val">${escape(valueFmt(bar.up || 0))}</span></div>
        <div class="adm2-lc-row"><span class="adm2-lc-dot" style="background:${escape(downColor)};"></span><span class="adm2-lc-name">${escape(downLabel)}</span><span class="adm2-lc-val">${escape(valueFmt(bar.down || 0))}</span></div>
        <div class="adm2-lc-row"><span class="adm2-lc-dot" style="background:${escape(netTone)};"></span><span class="adm2-lc-name">Net</span><span class="adm2-lc-val" style="color:${escape(netTone)};">${escape(valueFmt(net))}</span></div>
      `;
      tip.style.opacity = '1';
      const wrapRect = wrap.getBoundingClientRect();
      const colRect = col.getBoundingClientRect();
      const tipRect = tip.getBoundingClientRect();
      let left = colRect.left - wrapRect.left + colRect.width / 2 - tipRect.width / 2;
      left = Math.max(4, Math.min(left, wrapRect.width - tipRect.width - 4));
      tip.style.left = `${left}px`;
      tip.style.top = `${Math.max(0, colRect.top - wrapRect.top - tipRect.height - 6)}px`;
    });
    col.addEventListener('mouseleave', clear);
  });
  wrap.addEventListener('mouseleave', clear);
}

/**
 * Two-side diverging bar chart. bars: [{up, down}] where up = deposits,
 * down = withdrawals. Both should be raw numbers; we scale to fit.
 */
export function divergingBars({ bars, upColor = 'var(--a-ok)', downColor = '#E08177', height = 184 } = {}) {
  if (!bars || !bars.length) return emptyState('No flow data');
  const maxAbs = Math.max(1, ...bars.map((b) => Math.max(Math.abs(b.up || 0), Math.abs(b.down || 0))));
  const half = height / 2;
  const inner = bars
    .map((b) => {
      const up = Math.round(((b.up || 0) / maxAbs) * (half - 4));
      const down = Math.round(((b.down || 0) / maxAbs) * (half - 4));
      return `
        <div style="flex:1;display:flex;flex-direction:column;height:${height}px;">
          <div style="flex:1;display:flex;align-items:flex-end;justify-content:center;">
            <div style="width:62%;height:${up}px;background:${upColor};border-radius:3px 3px 0 0;"></div>
          </div>
          <div style="height:1px;background:var(--a-border);"></div>
          <div style="flex:1;display:flex;align-items:flex-start;justify-content:center;">
            <div style="width:62%;height:${down}px;background:${downColor};border-radius:0 0 3px 3px;"></div>
          </div>
        </div>
      `;
    })
    .join('');
  return `<div style="display:flex;align-items:stretch;gap:7px;">${inner}</div>`;
}

/**
 * Horizontal composition bars.
 * rows: [{ label, value, color, pct (0..100), display }]
 */
export function compositionRows(rows = []) {
  return rows
    .map(
      (r) => `
    <div style="margin-bottom:13px;">
      <div style="display:flex;align-items:center;justify-content:space-between;gap:10px;margin-bottom:6px;">
        <span style="display:inline-flex;align-items:center;gap:8px;font:500 12px 'IBM Plex Sans',sans-serif;color:var(--a-ink-2);">
          <span style="width:9px;height:9px;border-radius:2px;background:${escape(r.color)};"></span>
          ${escape(r.label)}
        </span>
        <span style="font:500 12px 'IBM Plex Mono',monospace;color:var(--a-muted);">${escape(r.display || String(r.value))}</span>
      </div>
      <div class="adm2-bar-track"><div class="adm2-bar-fill" style="width:${Math.max(0, Math.min(100, r.pct || 0))}%;background:${escape(r.color)};"></div></div>
    </div>
  `,
    )
    .join('');
}

/** Funnel stages — used by duel lifecycle. rows: [{ stage, value, dropText, pct, color }] */
export function funnelStages(rows = []) {
  return rows
    .map(
      (r) => `
    <div style="margin-bottom:16px;">
      <div style="display:flex;align-items:center;justify-content:space-between;margin-bottom:7px;">
        <span style="font:600 13px 'IBM Plex Sans',sans-serif;color:var(--a-ink);">${escape(r.stage)}</span>
        <span style="display:flex;gap:12px;align-items:center;">
          <span style="font:500 12px 'IBM Plex Mono',monospace;color:var(--a-muted);">${escape(r.value)}</span>
          <span style="font:500 10px 'IBM Plex Mono',monospace;color:var(--a-faint);">${escape(r.dropText || '')}</span>
        </span>
      </div>
      <div style="height:30px;background:var(--a-track);border-radius:8px;overflow:hidden;">
        <div style="height:100%;width:${Math.max(0, Math.min(100, r.pct))}%;background:${escape(r.color)};border-radius:8px;"></div>
      </div>
    </div>
  `,
    )
    .join('');
}

// ---------------------------------------------------------------------------
// States: skeleton / empty / error
// ---------------------------------------------------------------------------

export function skeletonGrid(count = 8) {
  const one = `
    <div class="adm2-kpi">
      <div class="adm2-skl" style="height:11px;width:55%;"></div>
      <div class="adm2-skl" style="height:26px;width:75%;margin-top:16px;"></div>
      <div class="adm2-skl" style="height:9px;width:40%;margin-top:12px;"></div>
      <div class="adm2-skl" style="height:30px;width:100%;margin-top:16px;border-radius:8px;"></div>
    </div>
  `;
  return `<div class="adm2-grid">${one.repeat(count)}</div>
    <div class="adm2-skl" style="height:280px;width:100%;margin-top:18px;border-radius:14px;"></div>`;
}

export function emptyState(msg = 'No data for the selected range') {
  return `
    <div class="adm2-empty">
      <div class="adm2-empty-icon">∅</div>
      <div class="adm2-empty-title">${escape(msg)}</div>
      <div class="adm2-empty-sub">Try widening the date range or switch to a preset window.</div>
    </div>
  `;
}

export function errorState(err) {
  const msg = err && err.message ? err.message : String(err || 'Something went wrong');
  return `<div class="adm2-error">${escape(msg)}</div>`;
}

// ---------------------------------------------------------------------------
// asyncPanel: load → render lifecycle with skeleton + error handling.
// ---------------------------------------------------------------------------

/**
 * host: DOM element to render into.
 * load: () => Promise<data>
 * render: (data) => htmlString
 * options: { skeleton: htmlString }
 */
export async function asyncPanel(host, { load, render, skeleton } = {}) {
  if (!host) return;
  host.innerHTML = skeleton || skeletonGrid(6);
  try {
    const data = await load();
    host.innerHTML = render(data);
  } catch (err) {
    if (err instanceof ApiError && err.status === 401) {
      navigate('/login');
      return;
    }
    toast.fromError(err);
    host.innerHTML = errorState(err);
  }
}

// ---------------------------------------------------------------------------
// Date-range + granularity controls (Phase C endpoints).
// ---------------------------------------------------------------------------

/** Lagos (UTC+1) local "today" as ISO YYYY-MM-DD. */
function _todayIso() {
  const lagos = new Date(Date.now() + 60 * 60 * 1000);
  return lagos.toISOString().slice(0, 10);
}
function _daysAgoIso(days) {
  const lagos = new Date(Date.now() + 60 * 60 * 1000 - days * 86400 * 1000);
  return lagos.toISOString().slice(0, 10);
}

/**
 * The one shared range state — persisted across navigations via
 * sessionStorage so a click through the sidebar doesn't reset the
 * user's chosen window.
 */
const _RANGE_KEY = 'adm2-range';
export function loadRange() {
  const fallback = { preset: '30d', start: _daysAgoIso(29), end: _todayIso(), gran: 'day' };
  try {
    const raw = sessionStorage.getItem(_RANGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    // Migration: old sessions may carry `preset: '7d'` (retired when the
    // dropdown replaced the pill row) or any preset the current build
    // doesn't recognise. Map anything unknown to the default so the
    // topbar trigger doesn't render as blank / 'Range'.
    if (!PRESET_DEFS[parsed.preset] && parsed.preset !== 'custom') {
      return { ...fallback, gran: parsed.gran || fallback.gran };
    }
    return parsed;
  } catch {
    return fallback;
  }
}
export function saveRange(state) {
  try {
    sessionStorage.setItem(_RANGE_KEY, JSON.stringify(state));
  } catch {
    /* private mode / quota — the range just won't persist */
  }
}

/** ISO YYYY-MM-DD for the 1st of the current Lagos month. */
function _monthStartIso() {
  const lagos = new Date(Date.now() + 60 * 60 * 1000);
  return `${lagos.getUTCFullYear()}-${String(lagos.getUTCMonth() + 1).padStart(2, '0')}-01`;
}
/** ISO YYYY-MM-DD for Jan 1 of the current Lagos year. */
function _yearStartIso() {
  const lagos = new Date(Date.now() + 60 * 60 * 1000);
  return `${lagos.getUTCFullYear()}-01-01`;
}
/** ISO YYYY-MM-DD for Monday of the current Lagos ISO week. Monday = 1,
 *  Sunday = 7 per ISO-8601; UTC getUTCDay() returns Sunday=0..Saturday=6.
 */
function _weekStartIso() {
  const lagos = new Date(Date.now() + 60 * 60 * 1000);
  const dow = lagos.getUTCDay();
  const daysSinceMonday = (dow + 6) % 7; // Mon→0, Sun→6
  const monday = new Date(lagos.getTime() - daysSinceMonday * 86400 * 1000);
  return monday.toISOString().slice(0, 10);
}

/**
 * All accepted preset kinds and the ranges they produce. Keeping this
 * table beside applyPreset so a reviewer can see the full menu at once —
 * matches the eight-item dropdown in the topbar.
 *
 * `all` sends no start/end so the backend endpoints treat it as
 * "unbounded". Every /admin/metrics/* endpoint defaults to the last-30-day
 * window ONLY when at least one bound is missing; passing neither is
 * still valid — see _parse_lagos_range in admin_metrics.py, which returns
 * (today - 30, today) when both are None. To force truly-unbounded we set
 * start/end to null AND the shell strips them from the query string; the
 * endpoints then use `epoch_start → today` for windowed reads via
 * read_daily_series (which just doesn't apply a lower bound), and the
 * live-compute fallback reads whichever aggregate function tolerates a
 * None start/end (all the aggregators do — they treat missing bounds as
 * "no filter"). Documented here rather than in each endpoint because
 * this is a client-side contract.
 */
const PRESET_DEFS = {
  today:      () => ({ start: _todayIso(),      end: _todayIso() }),
  this_week:  () => ({ start: _weekStartIso(),  end: _todayIso() }),
  this_month: () => ({ start: _monthStartIso(), end: _todayIso() }),
  this_year:  () => ({ start: _yearStartIso(),  end: _todayIso() }),
  '30d':      () => ({ start: _daysAgoIso(29),  end: _todayIso() }),
  '90d':      () => ({ start: _daysAgoIso(89),  end: _todayIso() }),
  all:        () => ({ start: null,             end: null       }),
};

/** Apply a preset to a range state — returns a new state. Unknown
 *  presets (e.g. 'custom') leave the current start/end alone; the
 *  caller flips into the picker mode instead. */
export function applyPreset(state, preset) {
  const fn = PRESET_DEFS[preset];
  if (!fn) return { ...state, preset };
  const { start, end } = fn();
  return { ...state, preset, start, end };
}

/** Human-readable label per preset. Used by rangeLabel + the dropdown
 *  trigger + the dropdown item list — one source so they can't drift. */
export const PRESET_LABELS = {
  today: 'Today',
  all: 'All Time',
  custom: 'Custom Period',
  this_week: 'This Week',
  this_month: 'This Month',
  this_year: 'This Year',
  '30d': 'Last 30 days',
  '90d': 'Last 90 days',
};

/** Human-readable range label for the topbar pill. Includes the actual
 *  start → end dates so the admin can see at a glance what window is
 *  in scope. For All Time we skip the dates (there's nothing meaningful
 *  to show). */
export function rangeLabel(state) {
  if (state.preset === 'all') return PRESET_LABELS.all;
  const dates = state.start && state.end ? `${state.start} → ${state.end}` : '';
  if (state.preset === 'custom') return dates || 'Custom range';
  const label = PRESET_LABELS[state.preset];
  return label && dates ? `${label} · ${dates}` : label || dates || '';
}

/** Compare-vs label (right of the range pill). */
export function compareLabel(state) {
  const map = {
    today: 'vs yesterday',
    this_week: 'vs previous week',
    this_month: 'vs previous month',
    this_year: 'vs previous year',
    '30d': 'vs previous 30 days',
    '90d': 'vs previous 90 days',
    custom: 'vs previous period',
    all: '',
  };
  return map[state.preset] || '';
}

/**
 * Query string for Phase C endpoints (start/end/granularity). Empty when
 * the range is the backend default so we don't force-pass params.
 */
export function rangeQuery(state, { granularity = true } = {}) {
  const parts = [];
  if (state.start) parts.push(`start=${state.start}`);
  if (state.end) parts.push(`end=${state.end}`);
  if (granularity && state.gran && state.gran !== 'day') parts.push(`granularity=${state.gran}`);
  return parts.length ? `?${parts.join('&')}` : '';
}

/**
 * Query string for Phase 1 endpoints (from/to). Same params but named
 * differently on the older endpoints (financial, growth, ...).
 */
export function fromToQuery(state) {
  const parts = [];
  if (state.start) parts.push(`from=${state.start}T00:00:00Z`);
  if (state.end) parts.push(`to=${state.end}T23:59:59Z`);
  return parts.length ? `?${parts.join('&')}` : '';
}

// ---------------------------------------------------------------------------
// CSV export — authorised download for endpoints that support format=csv.
// ---------------------------------------------------------------------------

/**
 * Fetch the CSV with the bearer token attached, then trigger a download.
 * We can't just link `<a href>` because the endpoint needs Authorization.
 */
export async function downloadCsv(path, filename) {
  try {
    const url = path.startsWith('http') ? path : `${API_BASE}${path}`;
    const res = await fetch(url, {
      headers: {
        Accept: 'text/csv',
        Authorization: `Bearer ${auth.getAccessToken() || ''}`,
      },
      credentials: 'include',
    });
    if (!res.ok) throw new ApiError(`CSV download failed (${res.status})`, { status: res.status });
    const blob = await res.blob();
    const objUrl = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = objUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(objUrl), 400);
    toast.success('Download started.');
  } catch (err) {
    toast.fromError(err);
  }
}

// ---------------------------------------------------------------------------
// Small chip helper for sortable table headers etc.
// ---------------------------------------------------------------------------

/** Fetch a JSON payload; return null on any error (used for optional cards). */
export async function fetchOrNull(path) {
  try {
    return await api(path);
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Sortable table — client-side, self-contained.
// ---------------------------------------------------------------------------

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const _PAGE_SIZE_KEY_PREFIX = 'adm2-pagesize:';

function _loadPageSize(key, fallback) {
  if (!key) return fallback;
  try {
    const raw = sessionStorage.getItem(_PAGE_SIZE_KEY_PREFIX + key);
    const n = raw ? Number(raw) : NaN;
    return PAGE_SIZE_OPTIONS.includes(n) ? n : fallback;
  } catch {
    return fallback;
  }
}

function _savePageSize(key, size) {
  if (!key) return;
  try {
    sessionStorage.setItem(_PAGE_SIZE_KEY_PREFIX + key, String(size));
  } catch {
    /* private mode / quota — silently drop */
  }
}

/** Compact <select> + Prev/Next pager. Shared by client- and server-driven tables. */
export function pagerControls({
  pageSize,
  page,
  totalPages,
  total,
  shownFrom,
  shownTo,
  hasPrev,
  hasNext,
  sizeSelectId,
  prevId,
  nextId,
  sizeOptions = PAGE_SIZE_OPTIONS,
} = {}) {
  const opts = sizeOptions
    .map(
      (n) =>
        `<option value="${n}"${n === pageSize ? ' selected' : ''}>${n} / page</option>`,
    )
    .join('');
  const summary =
    total != null && shownFrom != null && shownTo != null
      ? `${fmtInt(shownFrom)}–${fmtInt(shownTo)} of ${fmtInt(total)}`
      : `Page ${fmtInt(page)} of ${fmtInt(totalPages)}`;
  return `
    <div class="adm2-pager">
      <div class="adm2-pager-size">
        <label for="${escape(sizeSelectId)}">Rows</label>
        <select id="${escape(sizeSelectId)}" class="adm2-select">${opts}</select>
      </div>
      <div class="adm2-pager-summary">${escape(summary)}</div>
      <div class="adm2-pager-nav">
        <button type="button" class="adm2-btn" id="${escape(prevId)}"${hasPrev ? '' : ' disabled'}${hasPrev ? '' : ' style="opacity:.4;cursor:not-allowed;"'}>← Prev</button>
        <button type="button" class="adm2-btn" id="${escape(nextId)}"${hasNext ? '' : ' disabled'}${hasNext ? '' : ' style="opacity:.4;cursor:not-allowed;"'}>Next →</button>
      </div>
    </div>
  `;
}

/**
 * Mount a click-sortable table into `host`.
 *
 * columns: [{
 *   key,          // stable id, referenced by defaultSort.key
 *   label,        // header text
 *   right = false,
 *   sortable = true,   // false → header renders plain, no click handler
 *   get(row),          // sort accessor (defaults to row[key])
 *   cell(row),         // returns cell HTML string (defaults to escaped raw value)
 *   className,         // extra class(es) applied to the <td>
 * }]
 *
 * defaultSort: { key, dir: 'asc'|'desc' }
 * rowAttrs(row) → string of extra <tr> attributes (e.g. data-live-id, style).
 * onRowClick(row, ev): if set, rows get cursor:pointer and a click handler.
 * emptyMessage: shown when rows is empty.
 * minWidth: number of pixels — the table's min-width for horizontal scroll.
 * pageSizeKey: sessionStorage key suffix for persisting the size choice.
 * defaultPageSize: initial rows-per-page (must be one of 25/50/100).
 */
export function mountSortableTable(
  host,
  {
    columns,
    rows,
    defaultSort,
    rowAttrs,
    onRowClick,
    emptyMessage = 'No rows.',
    minWidth = 720,
    pageSizeKey,
    defaultPageSize = 25,
  } = {},
) {
  if (!host) return;
  let sortKey = defaultSort?.key || null;
  let sortDir = defaultSort?.dir || 'desc';
  let pageSize = _loadPageSize(pageSizeKey, defaultPageSize);
  let page = 1;

  const paint = () => {
    const sorted = _sortRows(rows, columns, sortKey, sortDir);
    const total = sorted.length;
    const totalPages = Math.max(1, Math.ceil(total / pageSize));
    if (page > totalPages) page = totalPages;
    const sliceStart = (page - 1) * pageSize;
    const sliceEnd = Math.min(total, sliceStart + pageSize);
    const pageRows = sorted.slice(sliceStart, sliceEnd);
    host.innerHTML = _renderSortableTable({
      columns,
      rows: pageRows,
      sortKey,
      sortDir,
      minWidth,
      rowAttrs,
      onRowClick,
      emptyMessage,
      pager:
        total > 0
          ? pagerControls({
              pageSize,
              page,
              totalPages,
              total,
              shownFrom: sliceStart + 1,
              shownTo: sliceEnd,
              hasPrev: page > 1,
              hasNext: page < totalPages,
              sizeSelectId: 'adm2-tbl-size',
              prevId: 'adm2-tbl-prev',
              nextId: 'adm2-tbl-next',
            })
          : '',
    });
    host.querySelectorAll('[data-adm2-sort]').forEach((th) => {
      th.addEventListener('click', () => {
        const k = th.dataset.adm2Sort;
        if (k === sortKey) {
          sortDir = sortDir === 'asc' ? 'desc' : 'asc';
        } else {
          sortKey = k;
          // Text columns default to asc; numeric to desc (biggest first).
          const col = columns.find((c) => c.key === k);
          sortDir = col && col.text ? 'asc' : 'desc';
        }
        page = 1;
        paint();
      });
    });
    if (typeof onRowClick === 'function') {
      host.querySelectorAll('[data-adm2-row]').forEach((tr, idx) => {
        tr.addEventListener('click', (ev) => onRowClick(pageRows[idx], ev));
      });
    }
    const sizeSel = host.querySelector('#adm2-tbl-size');
    if (sizeSel) {
      sizeSel.addEventListener('change', (ev) => {
        const next = Number(ev.target.value);
        if (!PAGE_SIZE_OPTIONS.includes(next)) return;
        pageSize = next;
        _savePageSize(pageSizeKey, pageSize);
        page = 1;
        paint();
      });
    }
    const prev = host.querySelector('#adm2-tbl-prev');
    const next = host.querySelector('#adm2-tbl-next');
    if (prev) prev.addEventListener('click', () => { if (page > 1) { page -= 1; paint(); } });
    if (next) next.addEventListener('click', () => { if (page < totalPages) { page += 1; paint(); } });
  };
  paint();
}

function _renderSortableTable({
  columns,
  rows,
  sortKey,
  sortDir,
  minWidth,
  rowAttrs,
  onRowClick,
  emptyMessage,
  pager = '',
}) {
  if (!rows || !rows.length) {
    return `<div style="padding:32px 16px;text-align:center;color:var(--a-faint);">${escape(emptyMessage)}</div>${pager}`;
  }
  const th = (c) => {
    const active = c.key === sortKey;
    const arrow = active ? (sortDir === 'asc' ? '↑' : '↓') : '';
    const attrs = c.sortable === false
      ? ''
      : `data-adm2-sort="${escape(c.key)}" style="cursor:pointer;"`;
    return `<th class="${c.right ? 'right' : ''}" ${attrs}>${escape(c.label)}${arrow ? ` ${escape(arrow)}` : ''}</th>`;
  };
  const tds = (row) =>
    columns
      .map((c) => {
        const cls = [c.right ? 'right' : '', c.className || ''].filter(Boolean).join(' ');
        const inner = typeof c.cell === 'function' ? c.cell(row) : escape(String(row[c.key] ?? ''));
        return `<td${cls ? ` class="${escape(cls)}"` : ''}>${inner}</td>`;
      })
      .join('');
  const trs = rows
    .map((r) => {
      const extra = typeof rowAttrs === 'function' ? rowAttrs(r) : '';
      const clickHint = typeof onRowClick === 'function' ? ' style="cursor:pointer;"' : '';
      return `<tr data-adm2-row ${extra}${clickHint}>${tds(r)}</tr>`;
    })
    .join('');
  return `
    <div style="overflow-x:auto;">
      <table class="adm2-table" style="min-width:${minWidth}px;">
        <thead><tr>${columns.map(th).join('')}</tr></thead>
        <tbody>${trs}</tbody>
      </table>
    </div>
    ${pager}
  `;
}

function _sortRows(rows, columns, key, dir) {
  if (!key) return rows.slice();
  const col = columns.find((c) => c.key === key);
  if (!col) return rows.slice();
  const get = typeof col.get === 'function' ? col.get : (r) => r[key];
  const mul = dir === 'asc' ? 1 : -1;
  return rows
    .slice()
    .sort((a, b) => {
      const va = get(a);
      const vb = get(b);
      // Nulls / undefined always sort to the bottom, regardless of direction.
      if (va == null && vb == null) return 0;
      if (va == null) return 1;
      if (vb == null) return -1;
      if (typeof va === 'number' && typeof vb === 'number') return (va - vb) * mul;
      return String(va).toLowerCase().localeCompare(String(vb).toLowerCase()) * mul;
    });
}

// Re-export escape so screen files can `import { escape } from './common'`.
export { escape };
